import { MailOutlined } from "@ant-design/icons";
import { requestPasswordReset } from "@bodhistreams/api/src";
import useUIStore from "@bodhistreams/stores/src/uiStore";
import { Alert, Button, Form, Input } from "antd";
import React, { useEffect, useState } from "react";
import Logo from "../../Layout/Logo";

const CustomModal = ({ isOpen, onClose, children, title }) => {
	useEffect(() => {
		// Prevent body scrolling when modal is open
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "auto";
		}
		return () => {
			document.body.style.overflow = "auto";
		};
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 1000,
				backgroundColor: "rgba(0, 0, 0, 0.5)",
			}}
			onClick={onClose}
		>
			<div
				style={{
					background: "white",
					borderRadius: "24px",
					boxShadow: "0 4px 30px rgba(0, 0, 0, 0.2)",
					width: "420px",
					maxWidth: "95%",
					animation: "fadeIn 0.3s ease-out",
					overflow: "hidden",
				}}
				onClick={(e) => e.stopPropagation()}
			>
				{title}
				<div style={{ padding: "32px 35px 42px" }}>{children}</div>
			</div>
		</div>
	);
};

const ResetModal = () => {
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	const { isResetModalVisible, hideResetModal, switchToLoginFromReset } =
		useUIStore();
	const [form] = Form.useForm();

	const handleCancel = () => {
		setError(null);
		setEmailSent(false);
		form.resetFields();
		hideResetModal();
	};

	const onFinish = async (values) => {
		setError(null);
		setLoading(true);
		try {
			await requestPasswordReset(values.email);
			setEmailSent(true);
			form.resetFields();
		} catch (err) {
			setError(
				err.response?.data?.message || "Failed to request password reset",
			);
		}
		setLoading(false);
	};

	const modalTitle = (
		<div
			style={{
				padding: "24px 0 0",
				background: "white",
				borderRadius: "24px 24px 0 0",
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					marginBottom: 24,
				}}
			>
				<Logo.Favicon size={48} hyperlink={true} />
			</div>
			<h2 style={{ textAlign: "center", marginBottom: 0 }}>Reset Password</h2>
		</div>
	);

	return (
		<CustomModal
			isOpen={isResetModalVisible}
			onClose={handleCancel}
			title={modalTitle}
		>
			{emailSent ? (
				<Alert
					message="Password Reset Email Sent!"
					description={
						<div>
							<p>
								If an account with that email exists, we've sent you a password
								reset link.
							</p>
							<p>
								Please check your email inbox (and spam folder) and click the
								link to reset your password.
							</p>
							<p style={{ marginBottom: 0 }}>
								The link will expire in 1 hour for security reasons.
							</p>
						</div>
					}
					type="success"
					showIcon
					style={{ borderRadius: "12px" }}
					action={
						<Button
							type="primary"
							onClick={handleCancel}
							style={{
								borderRadius: "8px",
								background: "linear-gradient(135deg, #6e8efb 0%, #a777e3 100%)",
								border: "none",
							}}
						>
							Close
						</Button>
					}
				/>
			) : (
				<>
					<p style={{ textAlign: "center", marginBottom: 24, color: "#666" }}>
						Enter your email address and we'll send you a link to reset your
						password.
					</p>

					{error && (
						<Alert
							message={error}
							type="error"
							showIcon
							style={{ marginBottom: 16, borderRadius: "12px" }}
						/>
					)}

					<Form form={form} name="reset" onFinish={onFinish}>
						<Form.Item
							name="email"
							rules={[
								{ required: true, message: "Please input your email!" },
								{ type: "email", message: "Please enter a valid email!" },
							]}
						>
							<Input
								prefix={<MailOutlined />}
								placeholder="Email"
								style={{ borderRadius: "12px", height: "40px" }}
							/>
						</Form.Item>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
								style={{
									width: "100%",
									borderRadius: "12px",
									height: "40px",
									background:
										"linear-gradient(135deg, #6e8efb 0%, #a777e3 100%)",
									border: "none",
								}}
								loading={loading}
							>
								Send Reset Link
							</Button>
						</Form.Item>
					</Form>

					<div style={{ textAlign: "center", marginTop: 24, fontSize: "14px" }}>
						Remember your password?{" "}
						<Button
							type="link"
							onClick={switchToLoginFromReset}
							style={{ padding: 0 }}
						>
							Back to Login
						</Button>
					</div>
				</>
			)}
		</CustomModal>
	);
};

export default ResetModal;
